import React, { useState, useRef, useEffect } from 'react'
import SALink from 'components/SALink'
import useOnScreen from 'hooks/onScreen'
import AboutArtistConnector, { ConnectorProps as AboutArtistProps } from '../connectors/AboutArtist'
import { Wrapper, ChevronIcon } from './styles/aboutArtist'
import ArtistBadges from './ArtistBadges'
import { displayStringWithoutLineBreak, accordionScroll, getTruncatedText } from '../helpers'
import classNames from 'classnames'
import { capitalizeString } from 'lib/helpers'

export default AboutArtistConnector((props: AboutArtistProps) => {
  const {
    artistCountry,
    artistName,
    artistProfileURL,
    artistAvatar,
    screenWidth,
    artworkID,
    artistAbout,
  } = props
  const aboutRef: Record<string, any> = useRef(null)
  const artistSectionRef: Record<string, any> = useRef(null)
  const artistNameRef: Record<string, any> = useRef(null)
  const { isSetup, isViewable, isFeatureAvailable } = useOnScreen(artistNameRef)
  const [showContent, setShowContent] = useState(false)
  const artistAboutText = getTruncatedText(artistAbout)
  const cleanArtistAbout = artistAboutText.includes('\n')
    ? displayStringWithoutLineBreak(artistAboutText)
    : artistAboutText
  // find the width of description <p> tag
  const artistAboutWidth = aboutRef.current ? aboutRef.current.offsetWidth : 0
  const artistAboutTextLengthValue = (artistAboutWidth * 955) / 1020
  const capitalizedArtistName = capitalizeString(artistName)

  const getAboutText = () => {
    return !showContent ? cleanArtistAbout : artistAboutText
  }

  const toggleSection = (event) => {
    const scrollBelowRef: boolean =
      artistNameRef.current && artistNameRef.current.getBoundingClientRect
        ? artistNameRef.current.getBoundingClientRect().y < 237
        : false
    const isScrollMobile =
      isFeatureAvailable &&
      isSetup &&
      showContent &&
      (!isViewable || (isViewable && scrollBelowRef))
    const isScrollDesktop = isFeatureAvailable && isSetup && !isViewable && showContent
    setShowContent(!showContent)

    if (screenWidth < 1024) {
      if (isScrollMobile) {
        accordionScroll(artistSectionRef, screenWidth, 135, 30)
      }
    } else if (isScrollDesktop) {
      accordionScroll(artistSectionRef, screenWidth, 135, 30)
    }
  }

  useEffect(() => {
    setShowContent(false)
  }, [artworkID])
  return (
    <Wrapper data-type='about-artist'>
      <div data-type='row' ref={artistSectionRef}>
        <div data-type='info-row'>
          {artistAvatar && (
            <div data-type='artist-image'>
              <SALink href={artistProfileURL} title='View profile'>
                <img
                  src={artistAvatar}
                  data-type='artist-avatar'
                  alt={capitalizedArtistName}
                  width='44px'
                  height='44px'
                />
              </SALink>
            </div>
          )}

          <div data-type='artist-info'>
            <div data-type='artist-info-col'>
              {capitalizedArtistName && (
                <h4 className='defaultTypographyH4'>
                  <SALink
                    data-type='artist-name'
                    href={artistProfileURL}
                    title='View profile'
                    ref={artistNameRef}
                  >
                    {capitalizedArtistName}
                  </SALink>
                </h4>
              )}

              {artistCountry && (
                <p className='defaultTypographySmall' data-type='artist-location'>
                  {artistCountry}
                </p>
              )}
            </div>
            <div data-type='profile-button'>
              <SALink data-type='artist-profile-link' href={artistProfileURL} title='View profile'>
                <p className='defaultTypographyCtaMedium'>View Profile</p>
              </SALink>
            </div>
          </div>
        </div>
      </div>
      {artistAbout && (
        <>
          <div data-type='row' ref={aboutRef}>
            <p
              className='defaultTypographyMedium'
              data-type={
                !showContent &&
                aboutRef.current &&
                cleanArtistAbout.length > artistAboutTextLengthValue
                  ? 'artist-about-clamped'
                  : 'artist-about'
              }
              dangerouslySetInnerHTML={{
                __html: `${(getAboutText() || '').replace(/\n/g, '<br />')}`,
              }}
            />
          </div>
          <div data-type='read-more'>
            {aboutRef.current && cleanArtistAbout.length > artistAboutTextLengthValue && (
              <button type='button' data-type='toggle-button' onClick={toggleSection}>
                <p className={classNames('defaultTypographyTiny', 'defaultTextBold')}>
                  {showContent ? 'READ LESS' : 'READ MORE'}
                </p>
                <ChevronIcon data-type={showContent ? 'rotate-chevron' : ''} />
              </button>
            )}
          </div>
        </>
      )}

      <ArtistBadges />
    </Wrapper>
  )
})
